import { useState, useEffect } from 'react';

/**
 * Hook to detect media query matches
 * Used for responsive layout switching between desktop and mobile
 */
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [query]);

  return matches;
};

/**
 * Hook to detect if the device is mobile
 * Uses the pointer media query (coarse = finger/touch, fine = mouse/trackpad)
 * combined with screen size to distinguish mobile devices from desktop
 * touchscreens and Windows laptops with touch support.
 */
export const useIsMobile = () => {
  const isSmallScreen = useMediaQuery('(max-width: 768px)');
  const isMediumScreen = useMediaQuery('(max-width: 1024px)');
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const hasAnyFinePointer = useMediaQuery('(any-pointer: fine)');

  // Small screen = always mobile
  // Medium screen + primary input is touch-only (no mouse/trackpad) = mobile/tablet
  // This avoids false positives on Windows laptops with touchscreens that also have a mouse
  return isSmallScreen || (isMediumScreen && isCoarsePointer && !hasAnyFinePointer);
};

export default useMediaQuery;
